import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useTypeaheadSearch } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";
import classNames from "classnames";
import { memo } from "react";
import { useLocation } from "react-router-dom";
import { SearchBarWithTypeahead } from "../SearchBarWithTypeahead";
import { useCaptureOmniboxURLParams } from "./hooks/useCaptureOmniboxURLParams";
import { useSharedOmniboxProps } from "./hooks/useSharedOmniboxProps";
import type { OmniboxProps } from "./Omnibox";
import { Omnibox } from "./Omnibox";
import styles from "./withPersistedOmniboxSurface.module.css";

export type HOCProps = {
  shouldMemoize?: boolean;
};

export function withPersistedOmniboxSurface(
  OmniboxComponent: React.ComponentType<OmniboxProps>,
) {
  return memo(
    function HOC(_props: HOCProps) {
      const { pathname } = useLocation();
      const isOmniboxEnabled = useFeatureFlagValueBool(
        "dash_2025_03_18_omnibox",
      );
      const isStartPage = pathname === "/";

      const sharedOmniboxProps = useSharedOmniboxProps();

      const typeaheadSearch = useTypeaheadSearch({
        isInstanced: false,
        featureLine: "search",
        actionSurfaceComponent: isOmniboxEnabled
          ? "omnibox"
          : "search_dropdown",
      });

      useCaptureOmniboxURLParams(typeaheadSearch);

      if (!isOmniboxEnabled) {
        return (
          <SearchBarWithTypeahead
            {...typeaheadSearch}
            {...sharedOmniboxProps}
          />
        );
      }

      return (
        <>
          <OmniboxComponent
            {...typeaheadSearch}
            {...sharedOmniboxProps}
            isOmniboxEnabled={isOmniboxEnabled}
            isSlimMode={!isStartPage}
          />
          <div
            className={classNames(styles.omniboxFocusOverlay, {
              [styles.visible]: typeaheadSearch.canShowTypeahead && isStartPage,
            })}
          />
        </>
      );
    },
    (_prevProps, nextProps) => {
      return nextProps.shouldMemoize === true;
    },
  );
}

export const PersistedOmnibox = withPersistedOmniboxSurface(Omnibox);
